"use client";

import { createLogger } from "@/lib/logger";
import type { MonthlyToAnnualUpgradeCapability } from "@/lib/revenuecat/product-change-config";
import { supabase } from "@/lib/supabaseClient";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

const logger = createLogger("useRevenueCatSubscription");

const SUBSCRIPTION_CACHE_TTL_MS = 30_000;
const CLIENT_RETRY_DELAY_MS = 1_000;

export type SubscriptionAccessStatus = "active" | "inactive" | "unknown";

export function hasVerifiedRevenueCatAccess(subscribed: boolean | null | undefined) {
  return subscribed === true;
}

export type SubscriptionInfo = {
  subscriptionId: string;
  productId: string | null;
  storeIdentifier: string | null;
  productType: string | null;
  subscriptionDuration: string | null;
  status: string;
  givesAccess: boolean;
  store: string | null;
  environment: string | null;
  hasPendingChange: boolean;
  currentPeriodStartsAt: string | null;
  currentPeriodEndsAt: string | null;
  autoRenewalStatus: string | null;
  managementUrl: string | null;
};

export type EntitlementInfo = {
  entitlementId: string;
  productId: string | null;
  expiresAt: string | null;
  purchaseDate: string | null;
};

export type SubscriptionResponse = {
  status: Exclude<SubscriptionAccessStatus, "unknown">;
  subscribed: boolean;
  activeProducts: string[];
  activeEntitlements?: EntitlementInfo[];
  accessSource?: "subscription" | "entitlement" | "none";
  subscriptions: SubscriptionInfo[];
  managementUrl?: string | null;
  detailsComplete?: boolean;
  monthlyToAnnualUpgrade?: MonthlyToAnnualUpgradeCapability | null;
};

type SubscriptionErrorResponse = {
  status?: "unknown";
  subscribed?: null;
  retryable?: boolean;
  error?: string;
};

type SubscriptionStoreSnapshot = {
  data: SubscriptionResponse | null;
  error: string | null;
  loading: boolean;
};

type SubscriptionStoreEntry = {
  appUserId: string;
  includeDetails: boolean;
  key: string;
  snapshot: SubscriptionStoreSnapshot;
  listeners: Set<() => void>;
  inFlight: Promise<void> | null;
  lastFetchedAt: number;
};

class SubscriptionRequestError extends Error {
  readonly retryable: boolean;

  constructor(message: string, retryable: boolean) {
    super(message);
    this.name = "SubscriptionRequestError";
    this.retryable = retryable;
  }
}

const EMPTY_SNAPSHOT: SubscriptionStoreSnapshot = {
  data: null,
  error: null,
  loading: false,
};

const subscriptionStores = new Map<string, SubscriptionStoreEntry>();

function normalizeAppUserId(appUserId?: string | null) {
  return appUserId?.trim().toLowerCase() || null;
}

function getSubscriptionStore(appUserId: string, includeDetails: boolean) {
  const key = `${appUserId}:${includeDetails ? "full" : "access"}`;
  const existing = subscriptionStores.get(key);
  if (existing) return existing;

  const entry: SubscriptionStoreEntry = {
    appUserId,
    includeDetails,
    key,
    snapshot: EMPTY_SNAPSHOT,
    listeners: new Set(),
    inFlight: null,
    lastFetchedAt: 0,
  };
  subscriptionStores.set(key, entry);
  return entry;
}

function updateSubscriptionStore(
  entry: SubscriptionStoreEntry,
  update: Partial<SubscriptionStoreSnapshot>
) {
  entry.snapshot = { ...entry.snapshot, ...update };
  entry.listeners.forEach((listener) => listener());
}

function wait(delayMs: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, delayMs));
}

async function requestSubscriptionStatus(
  appUserId: string,
  includeDetails: boolean,
  allowClientRetry = true
): Promise<SubscriptionResponse> {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    throw new SubscriptionRequestError(
      sessionError?.message || "Authenticated session required",
      false
    );
  }

  const searchParams = new URLSearchParams({ appUserId });
  if (includeDetails) {
    searchParams.set("details", "full");
  }

  const response = await fetch(`/api/revenuecat/subscriptions?${searchParams.toString()}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    cache: "no-store",
  });

  if (!response.ok) {
    const errorResponse = (await response.json().catch(() => ({}))) as SubscriptionErrorResponse;
    const retryable = response.status === 503 && errorResponse.retryable === true;

    if (retryable && allowClientRetry) {
      await wait(CLIENT_RETRY_DELAY_MS);
      return requestSubscriptionStatus(appUserId, includeDetails, false);
    }

    throw new SubscriptionRequestError(
      errorResponse.error || `Request failed: ${response.status}`,
      retryable
    );
  }

  const data = (await response.json()) as SubscriptionResponse;
  if (
    (data.status !== "active" && data.status !== "inactive") ||
    typeof data.subscribed !== "boolean"
  ) {
    throw new SubscriptionRequestError("Subscription response was incomplete", false);
  }

  if (includeDetails && data.detailsComplete !== true && allowClientRetry) {
    await wait(CLIENT_RETRY_DELAY_MS);
    return requestSubscriptionStatus(appUserId, includeDetails, false);
  }

  return data;
}

function refreshSubscriptionStore(entry: SubscriptionStoreEntry, force = false) {
  if (entry.inFlight) return entry.inFlight;

  if (
    !force &&
    entry.snapshot.data &&
    Date.now() - entry.lastFetchedAt < SUBSCRIPTION_CACHE_TTL_MS
  ) {
    return Promise.resolve();
  }

  updateSubscriptionStore(entry, { loading: true, error: null });

  entry.inFlight = requestSubscriptionStatus(entry.appUserId, entry.includeDetails)
    .then((data) => {
      entry.lastFetchedAt = entry.includeDetails && data.detailsComplete !== true ? 0 : Date.now();
      updateSubscriptionStore(entry, {
        data,
        error: null,
      });
      logger.info("Subscription data received from API", {
        status: data.status,
        subscribed: data.subscribed,
        hasManagementUrl: Boolean(data.managementUrl),
        activeProductCount: data.activeProducts.length,
        activeEntitlementCount: data.activeEntitlements?.length ?? 0,
        accessSource: data.accessSource,
        subscriptionCount: data.subscriptions.length,
        includeDetails: entry.includeDetails,
        detailsComplete: data.detailsComplete,
      });
    })
    .catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      const retainedData = entry.snapshot.data?.status === "active" ? entry.snapshot.data : null;

      logger.warn("Could not refresh subscription status", {
        error: message,
        retainedActiveAccess: Boolean(retainedData),
        retryable: error instanceof SubscriptionRequestError ? error.retryable : false,
      });
      updateSubscriptionStore(entry, {
        data: retainedData,
        error: "We couldn't check your subscription. Please try again.",
      });
    })
    .finally(() => {
      entry.inFlight = null;
      updateSubscriptionStore(entry, { loading: false });
    });

  return entry.inFlight;
}

export function useRevenueCatSubscription(
  appUserId?: string | null,
  options?: { includeDetails?: boolean }
) {
  const normalizedAppUserId = normalizeAppUserId(appUserId);
  const includeDetails = options?.includeDetails === true;
  const store = useMemo(
    () => (normalizedAppUserId ? getSubscriptionStore(normalizedAppUserId, includeDetails) : null),
    [includeDetails, normalizedAppUserId]
  );

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!store) return () => {};
      store.listeners.add(listener);
      return () => store.listeners.delete(listener);
    },
    [store]
  );
  const getSnapshot = useCallback(() => store?.snapshot ?? EMPTY_SNAPSHOT, [store]);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_SNAPSHOT);

  useEffect(() => {
    if (!store) return;
    void refreshSubscriptionStore(store);
  }, [store]);

  const refresh = useCallback(() => {
    if (!store) return;
    void refreshSubscriptionStore(store, true);
  }, [store]);

  const status: SubscriptionAccessStatus = snapshot.data?.status ?? "unknown";
  const subscribed = normalizedAppUserId ? (snapshot.data?.subscribed ?? null) : false;

  return {
    loading: snapshot.loading,
    error: snapshot.error,
    data: snapshot.data,
    status,
    subscribed,
    managementUrl: snapshot.data?.managementUrl ?? null,
    accessSource: snapshot.data?.accessSource ?? "none",
    activeEntitlements: snapshot.data?.activeEntitlements ?? [],
    refresh,
  } as const;
}

export function invalidateRevenueCatSubscriptionCache(appUserId?: string | null) {
  const normalizedAppUserId = normalizeAppUserId(appUserId);
  if (!normalizedAppUserId) return;

  for (const [key, entry] of subscriptionStores) {
    if (entry.appUserId !== normalizedAppUserId) continue;

    subscriptionStores.delete(key);
    updateSubscriptionStore(entry, {
      data: null,
      error: null,
      loading: false,
    });
  }
}

export function __resetRevenueCatSubscriptionStoreForTests() {
  subscriptionStores.clear();
}
